// CTV-682: Materials tab — print-ready collateral rendered from the station's
// own PublisherConfig.
//
// The tab is visible to everyone on a station's team; `materialsForRole`
// decides which sheets a given caller may pick from. Nothing here reads
// station-private data, so the gate is presentational only (contrast with
// Referrals, where commission figures forced server-side enforcement).
//
// CTV-840 (feedback 2026-09-03): the tab opens on a gallery, one section per
// category, and only opens a material once the reader picks one. It used to
// open on the first one-pager with a picker down the side, which read as "you
// are already looking at something" and did not scale past two categories.
// The selection lives in the URL (?material=<id>) so the browser's back button
// and a pasted link both do what a reader expects.

import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  Copy,
  Download,
  FileText,
  Film,
  History,
  Image,
  Link2,
  Mail,
  Printer,
} from 'lucide-react';
import { useSingleStationContext } from '../../components/self-service/admin/useAdminContext';
import AdminPageHeader from '../../components/self-service/admin/AdminPageHeader';
import {
  CATEGORY_BLURBS,
  canEditVideos,
  materialChips,
  materialFileName,
  materialGroupsForRole,
  withVideosGroup,
  type MaterialCategory,
  type MaterialDef,
  type MaterialId,
  type SheetMaterialId,
} from '../../components/self-service/materials/materialsCatalog';
import type { PublisherConfig } from '../../types/selfService';
import AdvertiserOnePager from '../../components/self-service/materials/AdvertiserOnePager';
import GuideOnePager from '../../components/self-service/materials/GuideOnePager';
import RepOnePager from '../../components/self-service/materials/RepOnePager';
import AdvertiserOnePagerV2 from '../../components/self-service/materials/archive/v2/AdvertiserOnePagerV2';
import GuideOnePagerV2 from '../../components/self-service/materials/archive/v2/GuideOnePagerV2';
import RepOnePagerV2 from '../../components/self-service/materials/archive/v2/RepOnePagerV2';
import DisplayAd from '../../components/self-service/materials/DisplayAd';
import {
  DISPLAY_AD_EXPORT_SCALE,
  displayAdExportScale,
  DISPLAY_AD_SPECS,
  displayAdLabel,
  SIGNATURE_SIZE_ORDER,
  SIGNATURE_SIZES,
  type SignatureSize,
  displayAdPreviewScale,
  type DisplayAdSize,
} from '../../components/self-service/materials/displayAdSizes';
import {
  copyNodeAsPng,
  copySignatureAsHtml,
  downloadNodeAsPng,
  downloadPngsAsZip,
  renderNodeToPng,
  renderNodeToPngUrl,
  zipFolder,
} from '../../components/self-service/materials/downloadPng';
import {
  storefrontHref,
  storefrontUrl,
} from '../../components/self-service/materials/storefrontUrl';
import { displayedName } from '../../data/publisherConfigs';
import { apiClient } from '../../lib/apiClient';
import { usePrintTitle } from '../../components/self-service/materials/usePrintTitle';
import {
  holdsWhiteText,
  type BannerLook,
} from '../../components/self-service/materials/sheetColor';
import FramePicker from '../../components/self-service/materials/FramePicker';
import AddVideoDialog from '../../components/self-service/materials/AddVideoDialog';
import { formatDuration } from '../../components/self-service/materials/videoDuration';
import { promotionIsLive } from '../../components/self-service/materials/promotionIsLive';
import type {
  DisplayAdStill,
  MaterialsVideo,
  SelfServicePromotion,
} from '../../types/selfService';

const LOOKS: BannerLook[] = ['dark', 'light'];
const LOOK_LABEL: Record<BannerLook, string> = { dark: 'Dark', light: 'Light' };
/** Who may add, rename, or delete a still: the roles the API admits. */
const STILL_EDITOR_ROLES = ['super_admin', 'organization_admin', 'manager'];

type SheetProps = {
  config: PublisherConfig;
  refCode?: string | null;
  look?: BannerLook;
  /** A saved still for the TV screen; display ads only. */
  screenImageUrl?: string;
  /** A promotion the ad is run for; display ads only. */
  promo?: AdPromo | null;
};

/** What a display ad needs to know about a promotion. */
interface AdPromo {
  code: string;
  bonusPercent: number;
}

// CTV-840: a display ad has no storefront link of its own (it's an image), so
// the ref code is shown beside the preview instead. CTV-856: the email
// signature also prints it, so it is passed through.
const displayAdSheet = (size: DisplayAdSize): React.FC<SheetProps> => {
  const Sheet: React.FC<SheetProps> = ({ config, refCode, look, screenImageUrl, promo }) => (
    <DisplayAd
      config={config}
      size={size}
      look={look}
      screenImageUrl={screenImageUrl}
      promo={promo}
      refCode={refCode}
    />
  );
  Sheet.displayName = `DisplayAd_${size}`;
  return Sheet;
};

// One entry per sheet id. Keeping this a map (rather than a chain of
// conditionals) means the catalogue and the renderer can't drift silently:
// a new id fails to typecheck until it has a component.
const SHEETS: Record<SheetMaterialId, React.FC<SheetProps>> = {
  'advertiser-onepager': AdvertiserOnePager,
  'guide-onepager': GuideOnePager,
  'rep-onepager': RepOnePager,
  'display-ad-320x50': displayAdSheet('320x50'),
  'display-ad-728x90': displayAdSheet('728x90'),
  'display-ad-300x250': displayAdSheet('300x250'),
  'display-ad-300x600': displayAdSheet('300x600'),
  'display-ad-1080x1080': displayAdSheet('1080x1080'),
  'email-signature': displayAdSheet('480x96'),
  'advertiser-onepager-v2': AdvertiserOnePagerV2,
  'guide-onepager-v2': GuideOnePagerV2,
  'rep-onepager-v2': RepOnePagerV2,
};
// CTV-863: a video (`video-<id>`) is the station's own mp4 and has no sheet;
// every caller branches on `material.video` before asking for one.
const sheetFor = (m: MaterialDef): React.FC<SheetProps> => SHEETS[m.id as SheetMaterialId];

/** A print sheet is letter proportion on screen (MaterialSheet). */
const SHEET_SIZE = { width: 850, height: 1100 };

/** The material's natural on-screen size, which the thumbnails scale from. */
const naturalSize = (m: MaterialDef) => (m.displayAd ? DISPLAY_AD_SPECS[m.displayAd] : SHEET_SIZE);

const CATEGORY_ICONS: Record<MaterialCategory, React.FC<{ className?: string }>> = {
  'One-Pagers': FileText,
  'Display Ads': Image,
  'Email Signature': Mail,
  Videos: Film,
  'Previous version': History,
};

const SEARCH_KEY = 'material';

/**
 * The width of an element, kept current as it resizes. Null until measured.
 * Measured in a layout effect, before the browser paints: with a plain
 * effect the preview painted one frame at its fallback scale and then
 * jumped to the fitted one (dev feedback 2026-09-09, "the preview changes
 * after a split second").
 */
function useElementWidth<T extends HTMLElement>(ref: React.RefObject<T>): number | null {
  const [width, setWidth] = React.useState<number | null>(null);
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setWidth(el.clientWidth || null);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return width;
}

/**
 * How tall the ad may be and still fit on screen: the viewport height less
 * the element's distance from the top of the page and the room the label
 * above and the link block below it need. Re-measured on resize. A layout
 * effect, for the same reason as useElementWidth.
 */
const PREVIEW_CHROME = 200;
/** The most a small ad is enlarged in the preview. */
const PREVIEW_MAX_ZOOM = 3;
/**
 * Density of the picture laid over the preview (feedback 2026-09-08: the
 * ad is markup, so right-click "Copy Image" was grabbing the TV chrome; a
 * real image of the whole ad on top makes copy, drag, and save-as work).
 * The picture is invisible (see the note by the markup), so this is the
 * density of what a right-click copies; the 4x file is for the download.
 */
const PREVIEW_SNAPSHOT_SCALE = 2;
/** How long the markup is left to settle (logo load, plate decision) before re-shooting. */
const SNAPSHOT_SETTLE_MS = 150;
/** The most a shot waits for the ad's own pictures to load before going ahead without them. */
const SNAPSHOT_IMAGE_WAIT_MS = 800;
/**
 * Resolves once every picture inside the ad (the logo, the TV chrome) has
 * loaded or failed, or after SNAPSHOT_IMAGE_WAIT_MS, whichever is first.
 */
function adPicturesSettled(node: HTMLElement): Promise<void> {
  const pictures = Array.from(node.querySelectorAll('img'), (img) =>
    img.complete
      ? Promise.resolve()
      : new Promise<void>((resolve) => {
          img.addEventListener('load', () => resolve(), { once: true });
          img.addEventListener('error', () => resolve(), { once: true });
        }),
  );
  return Promise.race([
    Promise.all(pictures).then(() => undefined),
    new Promise<void>((resolve) => window.setTimeout(resolve, SNAPSHOT_IMAGE_WAIT_MS)),
  ]);
}
function useFitHeight<T extends HTMLElement>(ref: React.RefObject<T>): number | null {
  const [height, setHeight] = React.useState<number | null>(null);
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const top = el.getBoundingClientRect().top + window.scrollY;
      setHeight(Math.max(240, window.innerHeight - top - PREVIEW_CHROME));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [ref]);
  return height;
}

const PublisherMaterialsPage: React.FC = () => {
  const { config, slug, user } = useSingleStationContext();

  const stationName = displayedName(config);

  // CTV-682: stamp the printing rep's own referral code onto every sheet's
  // storefront URL, so materials they hand out attribute campaigns back to
  // them. Managers and org admins usually have no salesperson row, in which
  // case this stays null and the URL prints clean.
  const [refCode, setRefCode] = React.useState<string | null>(null);
  React.useEffect(() => {
    let cancelled = false;
    apiClient
      .post<{ code: string | null }>('/salespeople/mine', { slug })
      .then((r) => {
        if (!cancelled) setRefCode(r.code ?? null);
      })
      .catch(() => {
        // Non-fatal: the sheets are still correct without a code.
        if (!cancelled) setRefCode(null);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);
  // CTV-863: the station's own commercials, a Videos group beside the
  // catalogue. Owned by the page after the first render, like the stills
  // below, so an add or delete shows at once without a config refetch.
  const [videos, setVideos] = React.useState<MaterialsVideo[]>(config.materialsVideos ?? []);
  const configVideosKey = (config.materialsVideos ?? []).map((v) => `${v.id}:${v.name}`).join('|');
  React.useEffect(() => {
    setVideos(config.materialsVideos ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configVideosKey]);
  const canManageVideos = canEditVideos(user?.rawRole);
  const [addVideoOpen, setAddVideoOpen] = React.useState(false);
  // The name-and-description form for the video on screen (feedback
  // 2026-09-10: a description is the uploader's, typed on upload or here).
  const [editingVideo, setEditingVideo] = React.useState(false);
  const groups = React.useMemo(
    () => withVideosGroup(materialGroupsForRole(user?.rawRole), videos, user?.rawRole),
    [user?.rawRole, videos],
  );
  const available = React.useMemo(() => groups.flatMap((g) => g.materials), [groups]);

  // The selection is the URL. An id the caller may not see (a role change, a
  // stale link, a draft they are not shown) falls back to the gallery rather
  // than rendering a sheet they may no longer pick.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedId = searchParams.get(SEARCH_KEY);
  const selected = available.find((m) => m.id === requestedId) ?? null;
  // Feedback 2026-09-03: the look (dark or light) rides in the URL too, so a
  // station switch or a shared link keeps it. Dark is the default and is left
  // out of the URL.
  const look: BannerLook = searchParams.get('look') === 'light' ? 'light' : 'dark';
  // CTV-856: the size the email signature is taken at; standard is the default
  // and stays out of the URL like dark does.
  const sigSize: SignatureSize = searchParams.get('size') === 'compact' ? 'compact' : 'standard';

  // Feedback 2026-09-03: the TV screen can show a saved still of the
  // station's own footage, and a station keeps several. The page owns the
  // list after the first render so a save or delete shows at once without a
  // config refetch; the chosen still rides in the URL like the look.
  const [stills, setStills] = React.useState<DisplayAdStill[]>(config.displayAdStills ?? []);
  // CTV-856: the gallery shows one category at a time. The tab rides in the
  // URL so a shared link or the browser's Back lands on the same one; the
  // first group is the default and stays out of the URL.
  const requestedGroup = searchParams.get('group');
  const activeGroup =
    groups.find((g) => categorySlug(g.category) === requestedGroup) ?? groups[0] ?? null;
  const selectGroup = (c: MaterialCategory) => {
    const next = new URLSearchParams(searchParams);
    if (groups[0]?.category === c) next.delete('group');
    else next.set('group', categorySlug(c));
    setSearchParams(next);
  };
  // Re-seed only when the config's list actually changes (a station switch,
  // a refetch), never merely because the config object was rebuilt: after a
  // delete the config still carries the old list until it is refetched, and
  // an identity-keyed effect put the deleted chip straight back.
  const configStillsKey = (config.displayAdStills ?? []).map((s) => `${s.id}:${s.name}`).join('|');
  React.useEffect(() => {
    setStills(config.displayAdStills ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configStillsKey]);
  const stillId = searchParams.get('still');
  const selectedStill = stills.find((s) => s.id === stillId) ?? null;
  const canEditStills = STILL_EDITOR_ROLES.includes(user?.rawRole ?? '');
  const [pickerOpen, setPickerOpen] = React.useState(false);

  // Feedback 2026-09-04: a display ad can be run for one of the station's
  // promotions. The ad shows the offer and the code, and the click-through
  // applies the code on arrival. Loaded once per station when an ad is open;
  // only promotions live today are offered.
  const [promos, setPromos] = React.useState<SelfServicePromotion[]>([]);
  React.useEffect(() => {
    if (!selected?.displayAd) return;
    let cancelled = false;
    apiClient
      .post<{ rows: SelfServicePromotion[] }>('/promotions/list', { slug })
      .then((r) => {
        if (!cancelled) setPromos((r.rows ?? []).filter(promotionIsLive));
      })
      .catch(() => {
        // A role that cannot list promotions simply gets no Promo row.
        if (!cancelled) setPromos([]);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, selected?.displayAd]);
  const promoCode = searchParams.get('promo');
  const selectedPromo = promos.find((p) => p.code === promoCode) ?? null;
  const adPromo: AdPromo | null = selectedPromo
    ? { code: selectedPromo.code, bonusPercent: selectedPromo.discount_percent }
    : null;

  const select = (
    id: MaterialId | null,
    opts: {
      look?: BannerLook;
      still?: string | null;
      promo?: string | null;
      size?: SignatureSize;
    } = {},
  ) => {
    const nextLook = opts.look ?? look;
    const nextStill = opts.still === undefined ? stillId : opts.still;
    const nextPromo = opts.promo === undefined ? promoCode : opts.promo;
    const nextSize = opts.size ?? sigSize;
    const next = new URLSearchParams(searchParams);
    if (id) next.set(SEARCH_KEY, id);
    else next.delete(SEARCH_KEY);
    // Back to the gallery lands on the tab the material came from, even when
    // the material was opened from a link that never set one.
    if (!id && selected && groups[0]?.category !== selected.category) {
      next.set('group', categorySlug(selected.category));
    }
    if (id && nextLook === 'light') next.set('look', 'light');
    else next.delete('look');
    if (id === 'email-signature' && nextSize === 'compact') next.set('size', 'compact');
    else next.delete('size');
    if (id && nextStill) next.set('still', nextStill);
    else next.delete('still');
    if (id && nextPromo) next.set('promo', nextPromo);
    else next.delete('promo');
    setSearchParams(next);
  };

  // CTV-682: the saved-PDF filename comes from document.title. The hook swaps
  // it for the print dialog's lifetime and restores it afterwards, covering
  // both the button below and Cmd-P.
  const { printNow } = usePrintTitle(selected ? materialFileName(selected, slug) : null);

  // CTV-840: display ads download as a PNG rendered from the preview node.
  // The export runs in the browser and takes a moment on the social square,
  // so the button shows that it's working and says so if the render fails.
  const previewRef = React.useRef<HTMLDivElement>(null);
  const [exportState, setExportState] = React.useState<'idle' | 'busy' | 'error'>('idle');
  React.useEffect(() => setExportState('idle'), [requestedId]);
  React.useEffect(() => setEditingVideo(false), [requestedId]);
  // Feedback 2026-09-08: the ad as an image on the clipboard, for pasting
  // straight into a post. Same render as the download.
  const [copyState, setCopyState] = React.useState<'idle' | 'busy' | 'copied' | 'error'>('idle');
  React.useEffect(() => setCopyState('idle'), [requestedId]);
  // CTV-856: the signature saves at 2x (see displayAdSizes); the rest at 4x.
  const exportScale = selected?.displayAd
    ? displayAdExportScale(selected.displayAd)
    : DISPLAY_AD_EXPORT_SCALE;
  // CTV-856: the signature copies as an HTML snippet (the picture at its
  // nominal size, inside its link), which is what a signature editor wants;
  // see copySignatureAsHtml for why a raw picture would not do.
  const signature = !!selected?.displayAd && !!DISPLAY_AD_SPECS[selected.displayAd].signature;
  // The size the picture is taken at. The node is always drawn at its spec
  // size; a signature at another size is the same render at a ratio.
  const output = signature ? SIGNATURE_SIZES[sigSize] : null;
  const outputRatio =
    output && selected?.displayAd ? output.width / DISPLAY_AD_SPECS[selected.displayAd].width : 1;
  const outputDensity = exportScale * outputRatio;
  const copyImage = async () => {
    const node = previewRef.current?.querySelector<HTMLElement>('[data-display-ad]');
    if (!node || !selected?.displayAd) return;
    setCopyState('busy');
    try {
      if (signature && output) {
        await copySignatureAsHtml(node, outputDensity, {
          width: output.width,
          height: output.height,
          href: storefrontHref(slug, refCode, {
            content: 'email_signature',
            medium: 'email',
            promo: adPromo?.code ?? null,
          }),
          alt: `Get your business on ${displayedName(config)}`,
        });
      } else {
        await copyNodeAsPng(node, exportScale);
      }
      setCopyState('copied');
      window.setTimeout(() => setCopyState((c) => (c === 'copied' ? 'idle' : c)), 2000);
    } catch {
      setCopyState('error');
    }
  };
  const downloadNow = async () => {
    const node = previewRef.current?.querySelector<HTMLElement>('[data-display-ad]');
    if (!selected || !node) return;
    setExportState('busy');
    try {
      await downloadNodeAsPng(
        node,
        materialFileName(selected, slug, {
          size: output ? `${output.width}x${output.height}` : null,
          look,
          version: selectedStill?.name,
          promo: adPromo?.code,
        }),
        outputDensity,
      );
      setExportState('idle');
    } catch {
      setExportState('error');
    }
  };

  // Feedback 2026-09-03: one click for every size. Each ad is rendered at its
  // natural size on an off-screen bench, exported, and saved as its own PNG in
  // turn (a web team wants the files, not an archive). Sequential, with a
  // beat between saves, because browsers throttle a burst of downloads and
  // Chrome asks once whether to allow several from this site.
  // CTV-856: the email signature renders like an ad but is its own material,
  // outside Download All Sizes.
  const adMaterials = React.useMemo(
    () => available.filter((m) => m.displayAd && !DISPLAY_AD_SPECS[m.displayAd].signature),
    [available],
  );
  const benchRef = React.useRef<HTMLDivElement>(null);
  // The files being saved right now (null when idle), how (separate files or
  // one zip), and whether the last run failed.
  const [bulk, setBulk] = React.useState<{
    targets: BulkTarget[];
    mode: BulkMode;
  } | null>(null);
  const bulkTargets = bulk?.targets ?? null;
  const [bulkFailed, setBulkFailed] = React.useState(false);
  React.useEffect(() => {
    if (!bulk) return;
    let cancelled = false;
    (async () => {
      try {
        const zipFiles: Array<{
          name: string;
          bytes: Uint8Array;
          folder?: string;
        }> = [];
        for (const t of bulk.targets) {
          const node = benchRef.current?.querySelector<HTMLElement>(
            `[data-bench="${bulkKey(t)}"] [data-display-ad]`,
          );
          if (!node) throw new Error(`no bench node for ${t.material.id}`);
          const name = materialFileName(t.material, slug, {
            look: t.look,
            version: t.still?.name,
            promo: t.promo?.code,
          });
          const density = t.material.displayAd
            ? displayAdExportScale(t.material.displayAd)
            : DISPLAY_AD_EXPORT_SCALE;
          if (bulk.mode === 'zip') {
            zipFiles.push({
              name,
              folder: t.folder,
              bytes: await renderNodeToPng(node, density),
            });
          } else {
            await downloadNodeAsPng(node, name, density);
            await new Promise((r) => window.setTimeout(r, 250));
          }
          if (cancelled) return;
        }
        if (bulk.mode === 'zip') downloadPngsAsZip(zipFiles, `Display-Ads_${slug}`);
      } catch {
        if (!cancelled) setBulkFailed(true);
      }
      if (!cancelled) setBulk(null);
    })();
    return () => {
      cancelled = true;
    };
  }, [bulk, slug]);
  const startBulk = (targets: BulkTarget[], mode: BulkMode) => {
    setBulkFailed(false);
    setBulk({ targets, mode });
  };
  const bench = bulkTargets && (
    <div
      ref={benchRef}
      aria-hidden
      data-testid="export-bench"
      style={{ position: 'fixed', left: -20000, top: 0 }}
    >
      {bulkTargets.map((t) => (
        <div key={bulkKey(t)} data-bench={bulkKey(t)}>
          {React.createElement(sheetFor(t.material), {
            config,
            refCode,
            look: t.look,
            screenImageUrl: t.still?.imageUrl,
            promo: t.promo,
          })}
        </div>
      ))}
    </div>
  );
  const downloadAllButton = (label: string) => (
    <DownloadSizesMenu
      label={label}
      materials={adMaterials}
      stills={stills}
      promos={promos}
      defaultLook={look}
      defaultStill={selectedStill?.id ?? null}
      defaultPromo={adPromo?.code ?? null}
      busy={bulkTargets !== null}
      onDownload={startBulk}
    />
  );
  const bulkError = bulkFailed && (
    <p role="alert" className="mb-4 text-[13px] text-red-700">
      Not every size could be saved. Try again, or download the sizes one at a time.
    </p>
  );

  // CTV-752: station-level switch. Hiding the NavLink is chrome; this guard
  // covers a direct URL. Super_admins pass through and get the banner below
  // instead, so they can review collateral for a station that has it off.
  const isSuperAdmin = user?.rawRole === 'super_admin';
  const materialsEnabled = config.materialsEnabled !== false;
  // CTV-759: the heading used to be repeated in each of these three
  // branches. One AdminPageHeader with a branch-dependent subtitle says the
  // same thing once, and the scope selector can't fall out of one branch.
  const unavailableReason =
    !materialsEnabled && !isSuperAdmin
      ? "Materials aren't available for this station."
      : available.length === 0
        ? 'No materials are available for your role yet.'
        : null;

  // Feedback 2026-09-04: this page uses the full main area; the shell already
  // pads it. Other admin tabs still cap themselves and are tracked separately.
  if (unavailableReason) {
    return (
      <div>
        <AdminPageHeader
          title="Materials"
          scopeMode="bound"
          currentSlug={slug}
          subtitle={unavailableReason}
        />
      </div>
    );
  }

  // CTV-863: one dialog, reachable from the Videos tab and from a video.
  const addVideoDialog = addVideoOpen && (
    <AddVideoDialog
      slug={slug}
      onCancel={() => setAddVideoOpen(false)}
      onSaved={(next, saved) => {
        setVideos(next);
        setAddVideoOpen(false);
        select(`video-${saved.id}`);
      }}
    />
  );

  const hiddenBanner = !materialsEnabled && (
    <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-800">
      The Materials tab is currently hidden from this station's team. Turn it back on in Channel
      settings.
    </div>
  );

  // ---- gallery ---------------------------------------------------------------
  if (!selected) {
    return (
      <div>
        {hiddenBanner}
        <AdminPageHeader
          title="Materials"
          scopeMode="bound"
          currentSlug={slug}
          subtitle={`${
            videos.length > 0
              ? 'Print sheets, web banners, and videos'
              : 'Print sheets and web banners'
          }, already branded for ${stationName}. Pick one to preview it, then print it or download it.`}
        />
        {/* CTV-856 (feedback 2026-09-09): one category at a time, as tabs,
            so the gallery never scrolls however many groups there are. This
            replaced stacked, foldable sections with a Jump-to row (CTV-840),
            which ran to three screens once a fourth group was in sight. */}
        {groups.length > 1 && (
          // Folder tabs (feedback 2026-09-09: underlined labels did not read
          // as tabs): the open one is white and joins the panel below it,
          // with the station's colour on its top edge, icon, and count, the
          // same accent the toolbar chips use.
          <div
            role="tablist"
            aria-label="Categories"
            className="relative z-10 flex flex-wrap items-end gap-1 px-2"
            style={{ '--chip-accent': holdsWhiteText(config.primaryColor) } as React.CSSProperties}
          >
            {groups.map((g) => {
              const Icon = CATEGORY_ICONS[g.category];
              const active = g.category === activeGroup?.category;
              return (
                <button
                  key={g.category}
                  type="button"
                  role="tab"
                  id={`materials-tab-${categorySlug(g.category)}`}
                  aria-selected={active}
                  aria-controls={`materials-${categorySlug(g.category)}`}
                  onClick={() => selectGroup(g.category)}
                  className={`-mb-px flex items-center gap-2 rounded-t-lg border border-b-0 px-4 py-2.5 text-[14px] font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                    active
                      ? 'border-t-[3px] border-slate-200 border-t-[var(--chip-accent,#0f172a)] bg-white text-slate-900 shadow-[0_1px_0_0_#fff]'
                      : 'border-slate-200/70 bg-slate-100 text-slate-500 hover:bg-slate-50 hover:text-slate-800'
                  }`}
                >
                  <Icon className={`h-4 w-4 ${active ? 'text-[var(--chip-accent,#0f172a)]' : ''}`} />
                  {g.category}
                  <span
                    className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                      active
                        ? 'bg-[var(--chip-accent,#0f172a)] text-white'
                        : 'bg-slate-200 text-slate-700'
                    }`}
                  >
                    {g.materials.length}
                  </span>
                </button>
              );
            })}
          </div>
        )}
        {bulkError}
        {bench}
        {addVideoDialog}
        {activeGroup && (
          <section
            role="tabpanel"
            id={`materials-${categorySlug(activeGroup.category)}`}
            aria-labelledby={`materials-tab-${categorySlug(activeGroup.category)}`}
            className="rounded-xl border border-slate-200 bg-white p-5"
          >
            <p className="mb-4 text-[13px] text-slate-500">
              {CATEGORY_BLURBS[activeGroup.category]}
            </p>
            {/* CTV-863: the Videos group is the one that can be empty (see
                withVideosGroup); it says so, and where to go from there. */}
            {activeGroup.category === 'Videos' && activeGroup.materials.length === 0 && (
              <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center">
                <Film className="h-6 w-6 text-slate-400" aria-hidden />
                <p className="max-w-md text-[13px] text-slate-600">
                  No videos yet. Add one made for {stationName} and the team can watch and
                  download it here.
                </p>
                {canManageVideos && (
                  <button
                    type="button"
                    onClick={() => setAddVideoOpen(true)}
                    className="rounded-md bg-blue-600 px-3 py-1.5 text-[13px] font-medium text-white hover:bg-blue-700"
                  >
                    + Add Video
                  </button>
                )}
              </div>
            )}
            <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(240px,1fr))]">
              {activeGroup.materials.map((m) => (
                <MaterialCard
                  key={m.id}
                  material={m}
                  stationName={stationName}
                  onSelect={() => select(m.id)}
                >
                  {m.video ? (
                    <VideoThumb video={m.video} />
                  ) : (
                    <MaterialThumb material={m}>
                      {React.createElement(sheetFor(m), { config, refCode })}
                    </MaterialThumb>
                  )}
                </MaterialCard>
              ))}
              {activeGroup.category === 'Videos' &&
                activeGroup.materials.length > 0 &&
                canManageVideos && (
                  <button
                    type="button"
                    onClick={() => setAddVideoOpen(true)}
                    className="flex min-h-[168px] flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 text-[13px] font-medium text-slate-500 hover:border-slate-500 hover:text-slate-900"
                  >
                    <Film className="h-5 w-5" aria-hidden />+ Add Video
                  </button>
                )}
            </div>
          </section>
        )}
      </div>
    );
  }

  // ---- one video (CTV-863) --------------------------------------------------
  // The station's own mp4: watched here, downloaded as it is. None of the
  // sheet machinery (look, still, promo, PNG export) applies.
  if (selected.video) {
    const video = selected.video;
    const videoSiblings = groups.find((g) => g.category === 'Videos')?.materials ?? [];
    const videoFile = `${materialFileName(selected, slug)}.mp4`;
    return (
      <div>
        {hiddenBanner}
        <button
          type="button"
          onClick={() => select(null)}
          className="mb-3 inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4" />
          All materials
        </button>
        <AdminPageHeader
          title={selected.title}
          scopeMode="bound"
          currentSlug={slug}
          subtitle={selected.blurb(stationName)}
          actions={
            <VideoActions
              video={video}
              slug={slug}
              fileName={videoFile}
              canManage={canManageVideos}
              editing={editingVideo}
              onAdd={() => setAddVideoOpen(true)}
              onEdit={() => setEditingVideo(true)}
              onDeleted={(next) => {
                setVideos(next);
                select(null);
              }}
            />
          }
        />
        {addVideoDialog}
        {editingVideo && (
          <EditVideoForm
            video={video}
            slug={slug}
            onCancel={() => setEditingVideo(false)}
            onSaved={(next) => {
              setVideos(next);
              setEditingVideo(false);
            }}
          />
        )}
        {videoSiblings.length > 1 && (
          <div
            className="mb-4 flex flex-wrap items-start justify-center gap-x-8 gap-y-4 rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-sm"
            style={{ '--chip-accent': holdsWhiteText(config.primaryColor) } as React.CSSProperties}
          >
            <Segment label="Videos">
              {videoSiblings.map((m) => (
                <Chip key={m.id} active={m.id === selected.id} onClick={() => select(m.id)}>
                  {m.title}
                </Chip>
              ))}
            </Segment>
          </div>
        )}
        <div className="rounded-xl bg-slate-200/50 p-5 md:p-7">
          {/* Keyed on the URL so flipping between videos loads the new one
              rather than leaving the old frame up. */}
          <video
            key={video.videoUrl}
            src={video.videoUrl}
            controls
            playsInline
            preload="metadata"
            data-testid="video-player"
            className="mx-auto block max-h-[70vh] w-full max-w-4xl rounded-lg bg-black"
          />
        </div>
      </div>
    );
  }

  // ---- one material ------------------------------------------------------------
  const siblings = groups.find((g) => g.category === selected.category)?.materials ?? [];
  return (
    <div>
      {hiddenBanner}
      <button
        type="button"
        onClick={() => select(null)}
        className="mb-3 inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="h-4 w-4" />
        All materials
      </button>
      <AdminPageHeader
        title={selected.title}
        scopeMode="bound"
        currentSlug={slug}
        subtitle={selected.blurb(stationName)}
        actions={
          selected.displayAd ? (
            <div className="flex items-center gap-2">
              {siblings.length > 1 && downloadAllButton('Download All Sizes')}
              <button
                type="button"
                onClick={copyImage}
                disabled={copyState === 'busy'}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:text-slate-400 whitespace-nowrap"
              >
                {copyState === 'copied' ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
                {copyState === 'busy'
                  ? 'Copying…'
                  : copyState === 'copied'
                    ? 'Copied'
                    : signature
                      ? 'Copy Signature'
                      : 'Copy Image'}
              </button>
              <button
                type="button"
                onClick={downloadNow}
                disabled={exportState === 'busy'}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 rounded-md whitespace-nowrap"
              >
                <Download className="w-4 h-4" />
                {exportState === 'busy' ? 'Preparing…' : 'Download PNG'}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={printNow}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md whitespace-nowrap"
            >
              <Printer className="w-4 h-4" />
              Print or Save as PDF
            </button>
          )
        }
      />

      {bulkError}
      {bench}
      {/* Feedback 2026-09-04: every choice sits in one toolbar of segmented
          groups, centred to match the preview beneath it. The rest of the
          category is the first group, so flipping between sizes or sheets
          never means going back to the gallery. */}
      <div
        className="mb-4 flex flex-wrap items-start justify-center gap-x-8 gap-y-4 rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-sm"
        /* Feedback 2026-09-04: the chosen chip wears the station's own
           colour, darkened only as far as white text needs. */
        style={{ '--chip-accent': holdsWhiteText(config.primaryColor) } as React.CSSProperties}
      >
        {siblings.length > 1 && (
          <Segment label={selected.displayAd ? 'Size' : selected.category}>
            {siblings.map((m) => (
              <Chip key={m.id} active={m.id === selected.id} onClick={() => select(m.id)}>
                {shortTitle(m)}
              </Chip>
            ))}
          </Segment>
        )}
        {/* CTV-856: the signature's own Size row (feedback 2026-09-09: reps
            differ on how big a signature should be). Not the ads' Size row,
            which switches materials; this scales one picture. */}
        {signature && (
          <Segment label="Size" role="group">
            {SIGNATURE_SIZE_ORDER.map((s) => (
              <Chip key={s} active={s === sigSize} onClick={() => select(selected.id, { size: s })}>
                {SIGNATURE_SIZES[s].label}
                <span className="ml-1.5 text-slate-400">
                  {SIGNATURE_SIZES[s].width}×{SIGNATURE_SIZES[s].height}
                </span>
              </Chip>
            ))}
          </Segment>
        )}
        {selected.displayAd && (
          <Segment label="Look" role="group">
            {LOOKS.map((l) => (
              <Chip key={l} active={l === look} onClick={() => select(selected.id, { look: l })}>
                {LOOK_LABEL[l]}
              </Chip>
            ))}
          </Segment>
        )}
        {/* CTV-856: the signature is not a size, so the row that explains
            "not on this size" would only confuse; it is left out. */}
        {selected.displayAd && !DISPLAY_AD_SPECS[selected.displayAd].signature && (
          <StillRow
            applies={DISPLAY_AD_SPECS[selected.displayAd].hasTv}
            stills={stills}
            selected={selectedStill}
            missingStill={!!stillId && !selectedStill}
            canEdit={canEditStills}
            slug={slug}
            onSelect={(id) => select(selected.id, { still: id })}
            onAdd={() => setPickerOpen(true)}
            onChanged={(next, keep) => {
              setStills(next);
              if (!keep || !next.some((s) => s.id === keep)) select(selected.id, { still: null });
            }}
          />
        )}
        {selected.displayAd && promos.length > 0 && (
          <Segment label="Promo" role="group">
            <Chip active={adPromo === null} onClick={() => select(selected.id, { promo: null })}>
              None
            </Chip>
            {promos.map((p) => (
              <Chip
                key={p.id}
                active={adPromo?.code === p.code}
                onClick={() => select(selected.id, { promo: p.code })}
              >
                {p.code}
                <span className="ml-1.5 text-slate-400">+{p.discount_percent}%</span>
              </Chip>
            ))}
          </Segment>
        )}
      </div>
      {selected.displayAd && DISPLAY_AD_SPECS[selected.displayAd].hasTv && pickerOpen && (
        <FramePicker
          slug={slug}
          config={config}
          look={look}
          onCancel={() => setPickerOpen(false)}
          onSaved={(next, saved) => {
            setStills(next);
            setPickerOpen(false);
            select(selected.id, { still: saved.id });
          }}
        />
      )}

      <div
        ref={previewRef}
        className={`max-w-full overflow-x-auto rounded-xl bg-slate-200/50 p-5 md:p-7${
          selected.displayAd ? ' w-full' : ' inline-block'
        }`}
      >
        {selected.displayAd ? (
          <DisplayAdPreview
            size={selected.displayAd}
            outputWidth={output?.width}
            outputHeight={output?.height}
            signatureActions={
              signature
                ? {
                    copy: copyImage,
                    copyLabel:
                      copyState === 'busy'
                        ? 'Copying…'
                        : copyState === 'copied'
                          ? 'Copied'
                          : 'Copy Signature',
                    copyBusy: copyState === 'busy',
                    download: downloadNow,
                    downloadLabel: exportState === 'busy' ? 'Preparing…' : 'Download PNG',
                    downloadBusy: exportState === 'busy',
                  }
                : undefined
            }
            slug={slug}
            refCode={refCode}
            promoCode={adPromo?.code ?? null}
            exportFailed={exportState === 'error'}
            copyFailed={copyState === 'error'}
            fileName={`${materialFileName(selected, slug, {
              look,
              version: selectedStill?.name,
              promo: adPromo?.code,
            })}.png`}
            snapshotKey={[slug, look, selectedStill?.id ?? '', adPromo?.code ?? '', refCode ?? ''].join('|')}
          >
            {React.createElement(sheetFor(selected), {
              config,
              refCode,
              look,
              screenImageUrl: selectedStill?.imageUrl,
              promo: adPromo,
            })}
          </DisplayAdPreview>
        ) : (
          React.createElement(sheetFor(selected), { config, refCode })
        )}
      </div>
    </div>
  );
};

const categorySlug = (c: MaterialCategory) => c.toLowerCase().replace(/[^a-z0-9]+/g, '-');

/** One display ad rendered one way, as a file to save. */
interface BulkTarget {
  material: MaterialDef;
  look: BannerLook;
  /** Null for the plain screen. */
  still: DisplayAdStill | null;
  /** Null for no promo. */
  promo: AdPromo | null;
  /**
   * Zip only: "Dark/MEMDAY10/Evening anchors/" and the like, look then promo
   * then TV screen. A level appears only when more than one value of it was
   * ticked, and the strips (no TV) sit above the screen level because no
   * screen version changes them.
   */
  folder?: string;
}
const bulkKey = (t: BulkTarget) =>
  `${t.material.id}:${t.look}:${t.promo?.code ?? 'none'}:${t.still?.id ?? 'plain'}`;
/** Separate PNG files, or one zip. Feedback 2026-09-03: both, the reader's choice. */
type BulkMode = 'files' | 'zip';

// The TV-screen versions: Plain, then each saved still by name. Managers can
// add a frame, and rename or delete the one that is showing.
const StillRow: React.FC<{
  /**
   * False on the strip sizes, which have no TV. The group still renders,
   * greyed out, so the toolbar keeps its shape as sizes are switched
   * (feedback 2026-09-04: the reflow from two lines to one was jarring).
   */
  applies: boolean;
  stills: DisplayAdStill[];
  selected: DisplayAdStill | null;
  /** The URL named a still the station no longer has. */
  missingStill: boolean;
  canEdit: boolean;
  slug: string;
  onSelect: (id: string | null) => void;
  onAdd: () => void;
  onChanged: (stills: DisplayAdStill[], keep: string | null) => void;
}> = ({ applies, stills, selected, missingStill, canEdit, slug, onSelect, onAdd, onChanged }) => {
  const [renaming, setRenaming] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const base = `/stations/${encodeURIComponent(slug)}/display-ad-stills`;

  const rename = async () => {
    if (!selected || !draft.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const r = await apiClient.patch<{ stills: DisplayAdStill[] }>(`${base}/${selected.id}`, {
        name: draft.trim(),
      });
      onChanged(r.stills, selected.id);
      setRenaming(false);
    } catch {
      setError("Couldn't rename it. Try again.");
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!selected) return;
    if (!window.confirm(`Delete "${selected.name}"? Ads using it go back to the plain screen.`))
      return;
    setBusy(true);
    setError(null);
    try {
      const r = await apiClient.delete<{ stills: DisplayAdStill[] }>(`${base}/${selected.id}`);
      onChanged(r.stills, null);
    } catch {
      setError("Couldn't delete it. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const showControls = applies && canEdit && selected !== null && !renaming;
  return (
    <Segment
      label="TV Screen"
      role="group"
      /* The controls for the showing frame hang centred under its chip, so
         the group never shifts as frames are switched; it leaves room. */
      className={showControls ? 'pb-5' : undefined}
      after={
        <>
          {!applies && <p className="mt-2 text-[12px] text-slate-400">Not on this size</p>}
          {applies && missingStill && (
            <p className="mt-2 max-w-[260px] text-center text-[12px] leading-snug text-amber-800">
              That saved frame is no longer here, so the plain screen is showing.
            </p>
          )}
          {renaming && selected && (
            <form
              className="mt-2 flex items-center justify-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                void rename();
              }}
            >
              <input
                type="text"
                aria-label="New name"
                value={draft}
                maxLength={60}
                autoFocus
                onChange={(e) => setDraft(e.target.value)}
                className="w-40 rounded-md border border-slate-300 px-2.5 py-1 text-[13px] focus:border-blue-500 focus:outline-none"
              />
              <button
                type="submit"
                disabled={busy || !draft.trim()}
                className="rounded-md bg-slate-900 px-3 py-1 text-[13px] font-medium text-white disabled:bg-slate-400"
              >
                Save Name
              </button>
              <button
                type="button"
                onClick={() => setRenaming(false)}
                className="rounded-md px-2 py-1 text-[13px] text-slate-600 hover:bg-slate-100"
              >
                Cancel
              </button>
            </form>
          )}
          {error && (
            <p role="alert" className="mt-2 text-center text-[12px] text-red-700">
              {error}
            </p>
          )}
        </>
      }
    >
      <Chip
        active={applies && selected === null}
        disabled={!applies}
        onClick={() => onSelect(null)}
      >
        Plain
      </Chip>
      {stills.map((s) => (
        <span key={s.id} className="relative inline-flex">
          <Chip
            active={applies && selected?.id === s.id}
            disabled={!applies}
            onClick={() => onSelect(s.id)}
          >
            {s.name}
          </Chip>
          {showControls && selected?.id === s.id && (
            <span className="absolute left-1/2 top-full mt-2 flex -translate-x-1/2 items-center gap-2 whitespace-nowrap text-[12px]">
              <button
                type="button"
                onClick={() => {
                  setDraft(s.name);
                  setRenaming(true);
                }}
                disabled={busy}
                className="text-slate-500 underline-offset-2 hover:text-slate-900 hover:underline"
              >
                Rename
              </button>
              <span aria-hidden className="text-slate-300">
                ·
              </span>
              <button
                type="button"
                onClick={remove}
                disabled={busy}
                className="text-red-700/80 underline-offset-2 hover:text-red-700 hover:underline"
              >
                Delete
              </button>
            </span>
          )}
        </span>
      ))}
      {canEdit && (
        <button
          type="button"
          onClick={onAdd}
          disabled={!applies}
          className="rounded-md border border-dashed border-slate-300 px-3 py-1.5 text-[13px] font-medium text-slate-500 hover:border-slate-500 hover:text-slate-900 disabled:text-slate-300 disabled:hover:border-slate-300"
        >
          + Add Frame
        </button>
      )}
    </Segment>
  );
};

/**
 * One labelled group of the toolbar: a small caption over a segmented
 * track of chips, with optional content beneath (a notice, a form).
 */
const Segment: React.FC<{
  label: string;
  role?: string;
  className?: string;
  after?: React.ReactNode;
  children: React.ReactNode;
}> = ({ label, role, className, after, children }) => (
  <div
    role={role}
    aria-label={role ? label : undefined}
    className={`flex flex-col items-center${className ? ` ${className}` : ''}`}
  >
    <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
      {label}
    </div>
    <div className="flex flex-wrap items-center justify-center gap-0.5 rounded-lg bg-slate-100 p-1">
      {children}
    </div>
    {after}
  </div>
);

const Chip: React.FC<{
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, disabled, onClick, children }) => (
  <button
    type="button"
    aria-pressed={active}
    disabled={disabled}
    onClick={onClick}
    className={`rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors disabled:text-slate-300 ${
      active
        ? 'bg-[var(--chip-accent,#0f172a)] text-white shadow-sm'
        : 'text-slate-600 hover:text-slate-900'
    }`}
  >
    {children}
  </button>
);

// Feedback 2026-09-03: "Download All" opens a checklist with every size
// ticked, so a web team that only runs two placements can untick the rest
// before saving. The confirm button counts what will be saved.
const PLAIN = 'plain';
const NO_PROMO = 'none';

const DownloadSizesMenu: React.FC<{
  label: string;
  materials: MaterialDef[];
  stills: DisplayAdStill[];
  /** The station's live promotions; the row is left out when there are none. */
  promos: SelfServicePromotion[];
  /** The look, version, and promo on screen; ticked by default, the others unticked. */
  defaultLook: BannerLook;
  defaultStill: string | null;
  defaultPromo: string | null;
  busy: boolean;
  onDownload: (targets: BulkTarget[], mode: BulkMode) => void;
}> = ({
  label,
  materials,
  stills,
  promos,
  defaultLook,
  defaultStill,
  defaultPromo,
  busy,
  onDownload,
}) => {
  const [open, setOpen] = React.useState(false);
  const [picked, setPicked] = React.useState<Set<MaterialId>>(new Set());
  const [looks, setLooks] = React.useState<Set<BannerLook>>(new Set());
  const [versions, setVersions] = React.useState<Set<string>>(new Set());
  const [promoPicks, setPromoPicks] = React.useState<Set<string>>(new Set());
  const [mode, setMode] = React.useState<BulkMode>('files');
  const rootRef = React.useRef<HTMLDivElement>(null);

  const openMenu = () => {
    setPicked(new Set(materials.map((m) => m.id)));
    setLooks(new Set([defaultLook]));
    setVersions(new Set([defaultStill ?? PLAIN]));
    setPromoPicks(new Set([defaultPromo ?? NO_PROMO]));
    setOpen(true);
  };
  const togglePromo = (code: string) =>
    setPromoPicks((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  const toggleVersion = (v: string) =>
    setVersions((prev) => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      return next;
    });
  const toggleLook = (l: BannerLook) =>
    setLooks((prev) => {
      const next = new Set(prev);
      if (next.has(l)) next.delete(l);
      else next.add(l);
      return next;
    });
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const toggle = (id: MaterialId) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const allPicked = picked.size === materials.length;
  // Files = ticked looks × ticked promos × (ticked TV sizes × ticked versions
  // + ticked strip sizes). The strips have no TV, so a version makes no
  // difference to them and they are saved once per look and promo. Order:
  // dark before light, no promo before the codes, plain before the saved
  // stills, sizes in catalogue order.
  const versionList: Array<DisplayAdStill | null> = [null, ...stills];
  const promoList: Array<AdPromo | null> = [
    null,
    ...promos.map((p) => ({ code: p.code, bonusPercent: p.discount_percent })),
  ];
  const showsTv = (m: MaterialDef) => !!m.displayAd && DISPLAY_AD_SPECS[m.displayAd].hasTv;
  const pickedTv = materials.filter((m) => picked.has(m.id) && showsTv(m)).length;
  const pickedStrips = materials.filter((m) => picked.has(m.id) && !showsTv(m)).length;
  const count =
    looks.size *
    promoPicks.size *
    (pickedTv * versions.size + (versions.size > 0 ? pickedStrips : 0));
  const confirm = () => {
    setOpen(false);
    const targets: BulkTarget[] = [];
    const lookLevel = looks.size > 1;
    const promoLevel = promoPicks.size > 1;
    const versionLevel = versions.size > 1;
    for (const l of LOOKS) {
      if (!looks.has(l)) continue;
      for (const promo of promoList) {
        if (!promoPicks.has(promo?.code ?? NO_PROMO)) continue;
        let firstVersion = true;
        for (const still of versionList) {
          if (!versions.has(still?.id ?? PLAIN)) continue;
          for (const m of materials) {
            if (!picked.has(m.id)) continue;
            if (!showsTv(m) && !firstVersion) continue;
            const folder =
              (lookLevel ? zipFolder(LOOK_LABEL[l]) : '') +
              (promoLevel ? zipFolder(promo?.code ?? 'No Promo') : '') +
              (versionLevel && showsTv(m) ? zipFolder(still?.name ?? 'Plain') : '');
            targets.push({
              material: m,
              look: l,
              promo,
              still: showsTv(m) ? still : null,
              folder,
            });
          }
          firstVersion = false;
        }
      }
    }
    onDownload(targets, mode);
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => (open ? setOpen(false) : openMenu())}
        disabled={busy}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:text-slate-400 whitespace-nowrap"
      >
        <Download className="w-4 h-4" />
        {busy ? 'Saving…' : label}
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Choose sizes to download"
          className="absolute right-0 top-full z-30 mt-2 w-72 rounded-xl border border-slate-200 bg-white p-3 shadow-lg"
        >
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[12px] font-semibold uppercase tracking-wide text-slate-500">
              Sizes to download
            </span>
            <button
              type="button"
              onClick={() => setPicked(allPicked ? new Set() : new Set(materials.map((m) => m.id)))}
              className="text-[12px] font-medium text-blue-700 hover:underline"
            >
              {allPicked ? 'Clear Sizes' : 'All Sizes'}
            </button>
          </div>
          <ul className="flex flex-col">
            {materials.map((m) => (
              <li key={m.id}>
                <label className="flex cursor-pointer items-center gap-2.5 rounded-md px-1.5 py-1.5 text-[13px] hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={picked.has(m.id)}
                    onChange={() => toggle(m.id)}
                    className="h-4 w-4 rounded border-slate-300"
                  />
                  <span className="font-medium text-slate-900">{shortTitle(m)}</span>
                  {m.displayAd && (
                    <span className="text-slate-500">{DISPLAY_AD_SPECS[m.displayAd].name}</span>
                  )}
                </label>
              </li>
            ))}
          </ul>
          {stills.length > 0 && (
            <div className="mt-2 border-t border-slate-100 pt-2">
              <span className="text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                TV Screen
              </span>
              <span className="ml-2 text-[11px] text-slate-400">
                300×250, 300×600, and 1080×1080 only
              </span>
              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
                {versionList.map((s) => {
                  const key = s?.id ?? PLAIN;
                  return (
                    <label
                      key={key}
                      className="flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1 text-[13px] hover:bg-slate-50"
                    >
                      <input
                        type="checkbox"
                        checked={versions.has(key)}
                        onChange={() => toggleVersion(key)}
                        className="h-4 w-4 rounded border-slate-300"
                      />
                      <span className="font-medium text-slate-900">{s?.name ?? 'Plain'}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
          <div className="mt-2 border-t border-slate-100 pt-2">
            <span className="text-[12px] font-semibold uppercase tracking-wide text-slate-500">
              Look
            </span>
            <div className="mt-1 flex gap-3">
              {LOOKS.map((l) => (
                <label
                  key={l}
                  className="flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1 text-[13px] hover:bg-slate-50"
                >
                  <input
                    type="checkbox"
                    checked={looks.has(l)}
                    onChange={() => toggleLook(l)}
                    className="h-4 w-4 rounded border-slate-300"
                  />
                  <span className="font-medium text-slate-900">{LOOK_LABEL[l]}</span>
                </label>
              ))}
            </div>
          </div>
          {promos.length > 0 && (
            <div className="mt-2 border-t border-slate-100 pt-2">
              <span className="text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                Promo
              </span>
              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
                {promoList.map((p) => {
                  const key = p?.code ?? NO_PROMO;
                  return (
                    <label
                      key={key}
                      className="flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1 text-[13px] hover:bg-slate-50"
                    >
                      <input
                        type="checkbox"
                        checked={promoPicks.has(key)}
                        onChange={() => togglePromo(key)}
                        className="h-4 w-4 rounded border-slate-300"
                      />
                      <span className="font-medium text-slate-900">{p?.code ?? 'None'}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
          <div
            className="mt-2 border-t border-slate-100 pt-2"
            role="radiogroup"
            aria-label="Save as"
          >
            <span className="text-[12px] font-semibold uppercase tracking-wide text-slate-500">
              Save as
            </span>
            <div className="mt-1 flex gap-3">
              {(
                [
                  ['files', 'Separate PNG files'],
                  ['zip', 'One zip file'],
                ] as Array<[BulkMode, string]>
              ).map(([m, label]) => (
                <label
                  key={m}
                  className="flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1 text-[13px] hover:bg-slate-50"
                >
                  <input
                    type="radio"
                    name="download-mode"
                    value={m}
                    checked={mode === m}
                    onChange={() => setMode(m)}
                    className="h-4 w-4 border-slate-300"
                  />
                  <span className="font-medium text-slate-900">{label}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="mt-2 flex items-center justify-end gap-2 border-t border-slate-100 pt-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-md px-3 py-1.5 text-[13px] font-medium text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirm}
              disabled={count === 0}
              className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-[13px] font-medium text-white hover:bg-blue-700 disabled:bg-blue-300"
            >
              <Download className="h-3.5 w-3.5" />
              {mode === 'zip' ? `Download ${count} as Zip` : `Download ${count} Selected`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * The chip label inside a category: the part of the title that differs
 * between siblings ("300×250", "for Advertisers"), since the category name
 * already sits beside the chips.
 */
function shortTitle(m: MaterialDef): string {
  return m.title.replace(/^Display Ad /, '').replace(/^One-Pager for /, '');
}

// A live, scaled-down render of the material. Sheets are top-cropped like a
// document preview (the header and headline are what identify them); ads are
// shown whole, centred, since their shape is the point.
const THUMB_HEIGHT = 168;
const MaterialThumb: React.FC<{
  material: MaterialDef;
  children: React.ReactNode;
}> = ({ material, children }) => {
  const frameRef = React.useRef<HTMLDivElement>(null);
  // Until measured (first paint, or a test renderer with no layout) assume
  // the grid's minimum card width; the observer corrects it a frame later.
  const frameWidth = useElementWidth(frameRef) ?? 240;
  const { width, height } = naturalSize(material);
  const inner = frameWidth - 24;
  const scale = material.displayAd
    ? Math.min(inner / width, (THUMB_HEIGHT - 24) / height)
    : inner / width;
  // The same hold the single-ad preview has (dev feedback 2026-09-09, seen
  // again in the gallery 2026-09-10): an ad's pictures land a few frames
  // after its markup and the brand row jumps when the logo does, so a
  // display-ad thumbnail stays invisible until they have settled, or the
  // wait runs out. Sheets are not held; their logo sits in a fixed slot.
  const adRef = React.useRef<HTMLDivElement>(null);
  const [pictured, setPictured] = React.useState(!material.displayAd);
  React.useEffect(() => {
    const node = adRef.current;
    if (!material.displayAd || !node) return;
    let cancelled = false;
    adPicturesSettled(node).then(() => {
      if (!cancelled) setPictured(true);
    });
    return () => {
      cancelled = true;
    };
  }, [material.displayAd]);
  return (
    <div
      ref={frameRef}
      aria-hidden
      className="relative overflow-hidden rounded-t-xl bg-slate-200/70"
      style={{ height: THUMB_HEIGHT }}
    >
      <div
        className="pointer-events-none absolute"
        data-testid={material.displayAd ? 'ad-thumb' : undefined}
        style={{
          left: (frameWidth - width * scale) / 2,
          top: material.displayAd ? (THUMB_HEIGHT - height * scale) / 2 : 12,
          width: width * scale,
          height: material.displayAd ? height * scale : THUMB_HEIGHT,
          opacity: pictured ? 1 : 0,
        }}
      >
        <div
          ref={adRef}
          style={{
            width,
            height,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

// CTV-863: a video card shows the video's own first frame. The fragment
// seeks a hair past zero, which is what makes Safari paint a frame at all
// for a video that has not been played. The length comes from the same
// metadata load and sits in the corner the way every video site puts it
// (feedback 2026-09-10); nothing is stored for it.
const VideoThumb: React.FC<{ video: MaterialsVideo }> = ({ video }) => {
  const [duration, setDuration] = React.useState<string | null>(null);
  return (
    <div
      className="relative overflow-hidden rounded-t-xl bg-slate-900"
      style={{ height: THUMB_HEIGHT }}
    >
      <video
        src={`${video.videoUrl}#t=0.1`}
        preload="metadata"
        muted
        playsInline
        tabIndex={-1}
        aria-hidden
        data-testid="video-thumb"
        className="h-full w-full object-cover"
        onLoadedMetadata={(e) => setDuration(formatDuration(e.currentTarget.duration))}
      />
      <Film aria-hidden className="absolute right-2 top-2 h-4 w-4 text-white/80" />
      {duration && (
        <span
          aria-label={`Length ${duration}`}
          className="absolute bottom-2 right-2 rounded bg-black/75 px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-white"
        >
          {duration}
        </span>
      )}
    </div>
  );
};

// CTV-863: everything you can do with the video on screen, in the page
// header where the other materials keep their actions (feedback 2026-09-10:
// a "This Video" group in the toolbar read oddly). Edit opens the form
// below the header; Delete asks first. The API answers with the whole
// list, which replaces the page's.
const VideoActions: React.FC<{
  video: MaterialsVideo;
  slug: string;
  /** The name the mp4 is saved under. */
  fileName: string;
  canManage: boolean;
  /** The edit form is open; its own buttons take over until it closes. */
  editing: boolean;
  onAdd: () => void;
  onEdit: () => void;
  onDeleted: (videos: MaterialsVideo[]) => void;
}> = ({ video, slug, fileName, canManage, editing, onAdd, onEdit, onDeleted }) => {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  React.useEffect(() => setError(null), [video.id]);

  const remove = async () => {
    if (!window.confirm(`Delete "${video.name}"? The team will no longer be able to download it.`))
      return;
    setBusy(true);
    setError(null);
    try {
      const r = await apiClient.delete<{ videos: MaterialsVideo[] }>(
        `/stations/${encodeURIComponent(slug)}/materials-videos/${video.id}`,
      );
      onDeleted(r.videos);
    } catch {
      setError("Couldn't delete it. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const quiet =
    'inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:text-slate-400 whitespace-nowrap';
  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      {canManage && (
        <>
          <button type="button" onClick={onEdit} disabled={busy || editing} className={quiet}>
            Edit
          </button>
          <button
            type="button"
            onClick={remove}
            disabled={busy || editing}
            className={`${quiet} text-red-700 hover:bg-red-50`}
          >
            Delete
          </button>
          <button type="button" onClick={onAdd} disabled={busy} className={quiet}>
            + Add Video
          </button>
        </>
      )}
      {/* Supabase Storage sets Content-Disposition from `download`, which is
          what makes a cross-origin link save rather than play. */}
      <a
        href={`${video.videoUrl}?download=${encodeURIComponent(fileName)}`}
        download={fileName}
        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md whitespace-nowrap"
      >
        <Download className="w-4 h-4" />
        Download MP4
      </a>
      {error && (
        <p role="alert" className="basis-full text-right text-[12px] text-red-700">
          {error}
        </p>
      )}
    </div>
  );
};

// CTV-863: the video's name and description, edited in place above the
// player, in the same white box the toolbar uses.
const EditVideoForm: React.FC<{
  video: MaterialsVideo;
  slug: string;
  onSaved: (videos: MaterialsVideo[]) => void;
  onCancel: () => void;
}> = ({ video, slug, onSaved, onCancel }) => {
  const [name, setName] = React.useState(video.name);
  const [description, setDescription] = React.useState(video.description);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const save = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await apiClient.patch<{ videos: MaterialsVideo[] }>(
        `/stations/${encodeURIComponent(slug)}/materials-videos/${video.id}`,
        { name: name.trim(), description: description.trim() },
      );
      onSaved(r.videos);
    } catch {
      setError("Couldn't save the changes. Try again.");
      setBusy(false);
    }
  };
  return (
    <form
      aria-label="Edit video"
      className="mb-4 flex flex-col gap-3 rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-sm"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <label className="flex flex-col gap-1 text-[13px]">
        <span className="font-medium text-slate-700">Name</span>
        <input
          type="text"
          value={name}
          maxLength={60}
          autoFocus
          disabled={busy}
          onChange={(e) => setName(e.target.value)}
          className="max-w-md rounded-md border border-slate-300 px-2.5 py-1.5 text-[13px] focus:border-blue-500 focus:outline-none"
        />
      </label>
      <label className="flex flex-col gap-1 text-[13px]">
        <span className="font-medium text-slate-700">
          Description <span className="font-normal text-slate-400">(optional)</span>
        </span>
        <textarea
          value={description}
          maxLength={300}
          rows={2}
          disabled={busy}
          onChange={(e) => setDescription(e.target.value)}
          className="max-w-2xl resize-y rounded-md border border-slate-300 px-2.5 py-1.5 text-[13px] focus:border-blue-500 focus:outline-none"
        />
        <span className="text-[12px] text-slate-500">
          Shown under the video's name, so the team knows what it is and who it is for.
        </span>
      </label>
      {error && (
        <p role="alert" className="text-[13px] text-red-700">
          {error}
        </p>
      )}
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={busy || !name.trim()}
          className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-blue-300"
        >
          {busy ? 'Saving…' : 'Save Changes'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="rounded-md px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

const MaterialCard: React.FC<{
  material: MaterialDef;
  stationName: string;
  onSelect: () => void;
  children: React.ReactNode;
}> = ({ material, stationName, onSelect, children }) => (
  <button
    type="button"
    onClick={onSelect}
    className="no-print flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white text-left shadow-sm transition-colors hover:border-slate-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
  >
    {children}
    <div className="flex flex-1 flex-col gap-1.5 p-4">
      <span className="text-[15px] font-semibold tracking-tight text-slate-900">
        {material.title}
      </span>

      {/* CTV-752: only super_admins ever receive drafts from materialsForRole,
          so this badge is their "toggled off" indicator, not a station-facing
          state. */}
      {material.draft && (
        <span className="self-start rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
          Draft: hidden from stations
        </span>
      )}
      {/* CTV-863: a video's description keeps the line breaks it was typed with. */}
      <p className="whitespace-pre-line text-[13px] leading-relaxed text-slate-500">
        {material.blurb(stationName)}
      </p>
      <div className="mt-auto pt-2">
        {/* Feedback 2026-09-08: what you get, at a glance; pinned to the card's foot. */}
        <span className="flex flex-wrap gap-1" aria-label="Details">
          {materialChips(material).map((chip) => (
            <span
              key={chip}
              className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600"
            >
              {chip}
            </span>
          ))}
        </span>
      </div>
    </div>
  </button>
);

// CTV-840: the ad at its real size (the social square scaled to fit), with
// the size named the way a web team names it and the address the ad should
// link to. The image can't carry a link itself, so the URL, with the rep's
// code, sits right under it with a one-click copy.
const DisplayAdPreview: React.FC<{
  size: DisplayAdSize;
  /** CTV-856: the size the signature is taken at; the node stays at its spec size. */
  outputWidth?: number;
  outputHeight?: number;
  /**
   * CTV-856: the toolbar's copy and download, repeated as buttons inside the
   * install steps (feedback 2026-09-09: "cant we just have 'copy signature'
   * do the copy"). Same handlers and state as the toolbar, so both say
   * Copied together.
   */
  signatureActions?: {
    copy: () => void;
    copyLabel: string;
    copyBusy: boolean;
    download: () => void;
    downloadLabel: string;
    downloadBusy: boolean;
  };
  slug: string;
  refCode: string | null;
  promoCode: string | null;
  exportFailed: boolean;
  copyFailed: boolean;
  /** The name the picture carries when saved from the browser. */
  fileName: string;
  /** Changes whenever the ad's inputs do (look, still, promo, station); re-shoots the picture. */
  snapshotKey: string;
  children: React.ReactNode;
}> = ({
  size,
  outputWidth,
  outputHeight,
  signatureActions,
  slug,
  refCode,
  promoCode,
  exportFailed,
  copyFailed,
  fileName,
  snapshotKey,
  children,
}) => {
  const { width, height, signature, name } = DISPLAY_AD_SPECS[size];
  // What the reader will get: the spec size, or the chosen signature size.
  const out = { width: outputWidth ?? width, height: outputHeight ?? height };
  const outRatio = out.width / width;
  const label = `${out.width}×${out.height} · ${name}`;
  // The rendered picture shown over the markup. Shot after the markup
  // settles, and again whenever it changes (a look, a still, the logo
  // loading, the plate appearing), so the picture never lags the markup.
  const [snapshot, setSnapshot] = React.useState<string | null>(null);
  const shotRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const host = shotRef.current;
    const node = host?.querySelector<HTMLElement>('[data-display-ad]');
    if (!host || !node) return;
    let cancelled = false;
    let timer: number | undefined;
    let run = 0;
    const shoot = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        const mine = (run += 1);
        // Every picture inside the ad has to have arrived, or the shot would
        // show a blank where it goes.
        adPicturesSettled(node)
          .then(() => renderNodeToPngUrl(node, PREVIEW_SNAPSHOT_SCALE))
          .then((url) => {
            if (!cancelled && mine === run) setSnapshot(url);
          })
          .catch(() => {
            // The markup stays visible underneath; a failed shot costs nothing.
          });
      }, SNAPSHOT_SETTLE_MS);
    };
    shoot();
    // A picture arriving later (the logo fetched from storage) is not a DOM
    // mutation; the load event, caught on the way down, re-shoots.
    node.addEventListener('load', shoot, true);
    if (typeof MutationObserver === 'undefined') {
      return () => {
        cancelled = true;
        window.clearTimeout(timer);
        node.removeEventListener('load', shoot, true);
      };
    }
    const mo = new MutationObserver(shoot);
    mo.observe(node, { attributes: true, childList: true, subtree: true, characterData: true });
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      node.removeEventListener('load', shoot, true);
      mo.disconnect();
    };
  }, [size, snapshotKey]);
  // A different size is a different box; the old picture would stretch into it.
  React.useEffect(() => setSnapshot(null), [size]);
  // The ad's own pictures (the logo, the TV chrome) land a few frames after
  // its markup, and the brand row grew when the logo did (dev feedback
  // 2026-09-09). The preview is held invisible until they have arrived, or
  // the wait runs out. Keyed by size rather than reset on change, so a new
  // size never paints a frame before it is hidden.
  const [picturesLoadedFor, setPicturesLoadedFor] = React.useState<DisplayAdSize | null>(null);
  const picturesLoaded = picturesLoadedFor === size;
  React.useEffect(() => {
    const node = shotRef.current?.querySelector<HTMLElement>('[data-display-ad]');
    if (!node) return;
    let cancelled = false;
    adPicturesSettled(node).then(() => {
      if (!cancelled) setPicturesLoadedFor(size);
    });
    return () => {
      cancelled = true;
    };
  }, [size]);
  // The ad shows at its real size when the panel has room, and scales down to
  // the panel's width when it doesn't (a 728 leaderboard in a narrow window
  // was clipping on the right). Measured, because the panel's width depends
  // on the viewport and the sidebar, not on anything this component knows.
  const frameRef = React.useRef<HTMLDivElement>(null);
  const available = useElementWidth(frameRef);
  const fitHeight = useFitHeight(frameRef);
  // Feedback 2026-09-04: the preview fills the panel, scaling the small
  // sizes up (to a limit, so a mobile strip is not a wall) and the large
  // ones down, and never taller than fits on screen without scrolling.
  // Before the panel is measured, the fixed cap holds.
  // `scale` is relative to what the reader gets (the "shown at" figure); the
  // node itself is drawn at the spec size, so it is transformed by the ratio too.
  // CTV-856: the signature shows at real size (feedback 2026-09-09). Its size
  // is the choice being made, and enlarging both choices to fill the panel
  // hid the difference; the enlarge-on-click is still there for a close look.
  const maxZoom = signature ? 1 : PREVIEW_MAX_ZOOM;
  const scale = available
    ? Math.min(maxZoom, available / out.width, (fitHeight ?? out.height) / out.height)
    : displayAdPreviewScale(size);
  // CTV-856: a click from a signature is an email arrival, tagged as such.
  const href = storefrontHref(slug, refCode, {
    content: signature ? 'email_signature' : `display_ad_${size}`,
    medium: signature ? 'email' : 'display',
    promo: promoCode,
  });
  const [copied, setCopied] = React.useState(false);
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be denied; the URL is still on screen to select.
    }
  };

  // A 300x250 at its real size is small on a desktop. Clicking it opens the
  // ad scaled to fill the viewport, rendered live (not a stretched capture),
  // so text and edges stay crisp. The enlarged copy renders AFTER the inline
  // one in the DOM, which is what keeps the download button's
  // querySelector('[data-display-ad]') pointed at the real-size node.
  const [zoom, setZoom] = React.useState<number | null>(null);
  // The enlarged copy gets its own picture, shot at a density to match the
  // zoom (feedback 2026-09-08: right-click there was still hitting the
  // chrome), so it copies and saves like the inline one and stays crisp.
  const [zoomSnapshot, setZoomSnapshot] = React.useState<string | null>(null);
  React.useEffect(() => {
    setZoomSnapshot(null);
    const node = shotRef.current?.querySelector<HTMLElement>('[data-display-ad]');
    if (zoom === null || !node) return;
    let cancelled = false;
    const density = Math.min(
      displayAdExportScale(size),
      Math.max(PREVIEW_SNAPSHOT_SCALE, Math.ceil(zoom)),
    );
    renderNodeToPngUrl(node, density)
      .then((url) => {
        if (!cancelled) setZoomSnapshot(url);
      })
      .catch(() => {
        // The live markup stays up; only the right-click copy is lost.
      });
    return () => {
      cancelled = true;
    };
  }, [zoom, snapshotKey, size]);
  const openZoom = () => {
    // Fit inside the overlay's padding and leave room for the caption line.
    const fit = Math.min((window.innerWidth - 64) / width, (window.innerHeight - 120) / height);
    setZoom(Math.max(fit, scale));
  };
  React.useEffect(() => {
    if (zoom === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setZoom(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoom]);

  return (
    <div ref={frameRef} className="flex w-full flex-col items-center gap-3">
      <div className="text-center text-[12px] font-semibold uppercase tracking-wide text-slate-500">
        {label}
        {Math.round(scale * 100) !== 100 && (
          <span className="ml-2 font-normal normal-case tracking-normal text-slate-400">
            shown at {Math.round(scale * 100)}%
          </span>
        )}
        <span className="ml-2 font-normal normal-case tracking-normal text-slate-400">
          Click to enlarge
        </span>
      </div>
      <button
        type="button"
        onClick={openZoom}
        aria-label="Enlarge the ad"
        className="relative block cursor-zoom-in text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        data-testid="ad-preview"
        // Opacity, not visibility: visibility is inherited, and the exporter
        // copies computed styles, so a hidden ancestor would blank the shot.
        style={{
          width: out.width * scale,
          height: out.height * scale,
          opacity: picturesLoaded ? 1 : 0,
        }}
      >
        {/* Pinned to the corner: a button centres its content, which would
            offset the nominal-size box before the transform enlarges it. */}
        <div
          ref={shotRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width,
            height,
            transform: `scale(${scale * outRatio})`,
            transformOrigin: 'top left',
          }}
        >
          {children}
        </div>
        {/* The picture of the ad, over the markup: this is what a right-click
            copies, a drag carries, and "save image as" writes. Invisible, so
            what the eye sees is the live markup: a 2x raster painted over
            a 240% preview on a retina screen went visibly soft the moment
            it landed (dev feedback 2026-09-09). The one cost is that a drag
            shows no ghost of the ad. */}
        {snapshot && (
          <img
            src={snapshot}
            alt={`${displayAdLabel(size)} display ad`}
            data-testid="ad-snapshot"
            title={fileName}
            className="absolute inset-0 h-full w-full opacity-0"
            draggable
          />
        )}
      </button>
      {zoom !== null && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`${displayAdLabel(size)}, enlarged`}
          onClick={() => setZoom(null)}
          className="fixed inset-0 z-50 flex cursor-zoom-out flex-col items-center justify-center gap-3 bg-slate-900/80 p-6"
        >
          <div
            style={{ width: width * zoom, height: height * zoom }}
            className="relative shadow-2xl"
          >
            <div
              style={{
                width,
                height,
                transform: `scale(${zoom})`,
                transformOrigin: 'top left',
              }}
            >
              {children}
            </div>
            {zoomSnapshot && (
              <img
                src={zoomSnapshot}
                alt={`${displayAdLabel(size)} display ad, enlarged`}
                data-testid="ad-snapshot-zoom"
                title={fileName}
                className="absolute inset-0 h-full w-full opacity-0"
                draggable
              />
            )}
          </div>
          <div className="text-[13px] text-slate-200">
            {displayAdLabel(size)} · shown at {Math.round(zoom * 100)}% · click anywhere or press
            Esc to close
          </div>
        </div>
      )}
      {exportFailed && (
        <p role="alert" className="text-[13px] text-red-700">
          The image couldn't be built. Try again, and if it keeps failing, tell us which station.
        </p>
      )}
      {copyFailed && (
        <p role="alert" className="text-[13px] text-red-700">
          The image couldn't be copied. Your browser may not allow it; use Download PNG instead.
        </p>
      )}
      {/* The address the ad sends readers to, laid out like a read-only
          field: label above, the URL in a bordered box, Copy at its end. */}
      {/* CTV-856: the signature's two install boxes need the panel's width. */}
      <div className={`flex flex-col items-center ${signature ? 'w-full max-w-3xl' : 'max-w-lg'}`}>
        <div className="mb-1 text-[12px] font-semibold uppercase tracking-wide text-slate-500">
          {signature ? 'Link for the Picture' : 'Click-through URL'}
        </div>
        {/* The box hugs the address; the note below wraps at the block's width. */}
        <div className="flex max-w-full items-stretch overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            title="Open in a new tab"
            className="flex min-w-0 items-center gap-2 px-3 py-2 font-mono text-[12px] text-slate-800 hover:bg-slate-50 hover:text-blue-700"
          >
            <Link2 className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden />
            <span className="truncate">{storefrontUrl(slug, refCode, promoCode)}</span>
          </a>
          <button
            type="button"
            onClick={copyLink}
            className={`inline-flex shrink-0 items-center gap-1.5 border-l border-slate-200 px-3 text-[12px] font-medium ${
              copied ? 'bg-emerald-50 text-emerald-700' : 'text-slate-700 hover:bg-slate-50'
            }`}
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? 'Copied' : 'Copy Link'}
          </button>
        </div>
        {signature ? (
          // CTV-856: the picture cannot carry the link itself; every mail
          // client attaches one in its signature editor. Two clients, the
          // ones the stations' teams use, in their own menu words.
          <div
            data-testid="signature-steps"
            className="mt-3 w-full text-left text-[13px] leading-relaxed text-slate-600"
          >
            <div className="mb-2 font-semibold text-slate-800">Add It to Your Email Signature</div>
            {/* One block per client family, each with the action it needs as
                a button in the step, so the reader never scrolls back up. */}
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="rounded-lg border border-slate-200 bg-white p-3">
                <div className="mb-1.5 text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                  Outlook or Apple Mail
                </div>
                <ol className="list-decimal space-y-1.5 pl-5">
                  <li>
                    <StepButton
                      onClick={signatureActions?.copy}
                      disabled={signatureActions?.copyBusy}
                      label={signatureActions?.copyLabel ?? 'Copy Signature'}
                    />
                  </li>
                  <li>Open your signature settings and paste.</li>
                </ol>
                <p className="mt-1.5 text-[12px] leading-snug text-slate-500">
                  The picture arrives at the right size with the link already on it.
                </p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white p-3">
                <div className="mb-1.5 text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                  Gmail
                </div>
                <ol className="list-decimal space-y-1.5 pl-5">
                  <li>
                    <StepButton
                      onClick={signatureActions?.download}
                      disabled={signatureActions?.downloadBusy}
                      label={signatureActions?.downloadLabel ?? 'Download PNG'}
                    />
                  </li>
                  <li>
                    In Settings, See All Settings, Signature, insert the picture with the image
                    button.
                  </li>
                  <li>
                    Click the picture, click the link button, and paste{' '}
                    <StepButton onClick={copyLink} label={copied ? 'Copied' : 'Copy Link'} />
                  </li>
                </ol>
                <p className="mt-1.5 text-[12px] leading-snug text-slate-500">
                  Gmail won't take a pasted picture, so it needs the file.
                </p>
              </div>
            </div>
            <p className="mt-2 text-[12px] leading-snug text-slate-500">
              The link carries tracking parameters that credit the referral to you
              {promoCode ? ' and apply the promo code' : ''}. Please do not remove them.
            </p>
          </div>
        ) : (
          <p className="mt-1.5 text-center text-[12px] leading-snug text-slate-500">
            The copied link carries tracking parameters that credit this ad
            {promoCode ? ', the promo code,' : ''} and the referral to your station. Please do
            not remove these parameters.
          </p>
        )}
      </div>
    </div>
  );
};

/** CTV-856: an action inside an install step, styled as a small button. */
const StepButton: React.FC<{ onClick?: () => void; disabled?: boolean; label: string }> = ({
  onClick,
  disabled,
  label,
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-0.5 text-[12px] font-medium text-slate-700 hover:bg-slate-50 disabled:text-slate-400"
  >
    {label === 'Copied' ? (
      <Check className="h-3 w-3" />
    ) : /download|preparing/i.test(label) ? (
      <Download className="h-3 w-3" />
    ) : (
      <Copy className="h-3 w-3" />
    )}
    {label}
  </button>
);

export default PublisherMaterialsPage;
